import {
  DotsThreeVertical,
  UsersThree,
} from '@phosphor-icons/react'
import { useId, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { LoadingState } from '@/components/loading-state'
import { friendlyErrorKey } from '@/lib/api/friendly-errors'
import { usePersonNameFormatter } from '@/lib/users/use-person-name'
import { formatNumber } from '@/lib/i18n/format'
import { currentLocale } from '@/lib/i18n'
import { kuniriIconWeight } from '@/lib/icons'

import {
  activityDetailApi,
  type ActivityDetail,
  type AttendanceStatus,
  type Participant,
  type ReportReason,
} from '../api'
import { feedbackAvailable } from '../access'
import { ConfirmDialog } from './confirm-dialog'
import type { ActivityNotice } from './feedback-notice'
import { UserAvatar } from './user-avatar'

export type ParticipantsStatus = 'loading' | 'ready' | 'restricted' | 'error'

interface ParticipantsPanelProps {
  activity: ActivityDetail
  status: ParticipantsStatus
  participants: Participant[]
  onRetry: () => void
  onChanged: () => void
  onNotice?: (notice: ActivityNotice) => void
}

const REPORT_REASONS: ReportReason[] = [
  'spam',
  'harassment',
  'inappropriate_content',
  'unsafe_behavior',
  'scam',
  'other',
]

const SCORES = [1, 2, 3, 4, 5]

const DEFAULT_SCORES = {
  punctualityScore: 5,
  reliabilityScore: 5,
  respectScore: 5,
}

type ActionTarget = {
  participant: Participant
  kind: 'report' | 'block' | 'feedback'
} | null

/**
 * Participant list for creator/confirmed viewers or a matching invite token.
 * Non-participants get the approved D-043 explanation instead of a 403 and no
 * request is issued to the participants endpoint.
 *
 * D-055 adds the creator attendance controls after the activity ends and
 * per-participant report/block/feedback actions, matching the upstream overflow
 * menu. Feedback for the creator can mark the participant absent first.
 */
export function ParticipantsPanel({
  activity,
  status,
  participants,
  onRetry,
  onChanged,
  onNotice,
}: ParticipantsPanelProps) {
  const { t } = useTranslation()
  const formatPerson = usePersonNameFormatter()
  const locale = currentLocale()
  const headingId = useId()
  const reasonId = useId()
  const descriptionId = useId()
  const punctualityId = useId()
  const reliabilityId = useId()
  const respectId = useId()
  const [target, setTarget] = useState<ActionTarget>(null)
  const [reason, setReason] = useState<ReportReason | ''>('')
  const [description, setDescription] = useState('')
  const [reasonError, setReasonError] = useState(false)
  const [scores, setScores] = useState(DEFAULT_SCORES)
  const [markAbsent, setMarkAbsent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [attendanceBusyId, setAttendanceBusyId] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [now, setNow] = useState(0)

  useEffect(() => {
    const timer = window.setTimeout(() => setNow(Date.now()), 0)
    return () => window.clearTimeout(timer)
  }, [])

  const endAt = activity.endAt ? Date.parse(activity.endAt) : Number.NaN
  const canManageAttendance =
    activity.viewerStatus === 'creator' &&
    Number.isFinite(endAt) &&
    now > 0 &&
    endAt < now

  function openAction(participant: Participant, kind: 'report' | 'block' | 'feedback') {
    setActionError(null)
    setReason('')
    setDescription('')
    setReasonError(false)
    setScores(DEFAULT_SCORES)
    setMarkAbsent(false)
    setTarget({ participant, kind })
  }

  function closeAction() {
    if (busy) return
    setTarget(null)
    setActionError(null)
  }

  async function setAttendance(
    participant: Participant,
    attendanceStatus: AttendanceStatus,
  ) {
    if (attendanceBusyId) return
    setAttendanceBusyId(participant.id)
    setActionError(null)
    setMessage(null)
    try {
      await activityDetailApi.setParticipantAttendance(
        activity.id,
        participant.id,
        attendanceStatus,
      )
      setMessage(
        t(
          attendanceStatus === 'attended'
            ? 'activityDetail.participants.attendanceConfirmSuccess'
            : 'activityDetail.participants.attendanceAbsentSuccess',
        ),
      )
      onChanged()
    } catch (error) {
      setMessage(friendlyErrorKey(error))
    } finally {
      setAttendanceBusyId(null)
    }
  }

  async function submitReport(participant: Participant) {
    if (!reason) {
      setReasonError(true)
      return
    }
    setBusy(true)
    setActionError(null)
    try {
      await activityDetailApi.report({
        targetType: 'user',
        targetId: participant.id,
        reason,
        ...(description.trim() ? { description: description.trim() } : {}),
      })
      setTarget(null)
      onNotice?.({ tone: 'success', key: 'activityDetail.actions.reportSuccess' })
    } catch (error) {
      setActionError(friendlyErrorKey(error))
    } finally {
      setBusy(false)
    }
  }

  async function submitBlock(participant: Participant) {
    setBusy(true)
    setActionError(null)
    try {
      await activityDetailApi.blockUser(participant.id)
      setTarget(null)
      onNotice?.({ tone: 'success', key: 'activityDetail.actions.blockSuccess' })
      onChanged()
    } catch (error) {
      setActionError(friendlyErrorKey(error))
    } finally {
      setBusy(false)
    }
  }

  async function submitFeedback(participant: Participant) {
    setBusy(true)
    setActionError(null)
    try {
      if (markAbsent) {
        await activityDetailApi.setParticipantAttendance(
          activity.id,
          participant.id,
          'absent',
        )
      }
      await activityDetailApi.submitFeedback(activity.id, {
        reviewedUserId: participant.id,
        ...scores,
      })
      setTarget(null)
      onNotice?.({ tone: 'success', key: 'activityDetail.feedback.success' })
      onChanged()
    } catch (error) {
      setActionError(friendlyErrorKey(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section
      aria-labelledby={headingId}
      aria-busy={status === 'loading' || undefined}
      className="space-y-3"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2
          id={headingId}
          className="font-head text-[18px] font-extrabold text-deep"
        >
          {t('activityDetail.participants.title')}
        </h2>
      </div>

      {message ? (
        <p role="status" className="mt-2 text-sm font-semibold text-teal">
          {message}
        </p>
      ) : null}

      {status === 'loading' ? (
        <LoadingState
          compact
          labelKey="activityDetail.participants.loading"
        />
      ) : null}

      {status === 'restricted' ? (
        <p className="text-[15px] leading-relaxed text-muted">
          {t('activityDetail.participants.restrictedBody')}
        </p>
      ) : null}

      {status === 'error' ? (
        <div className="mt-3 space-y-2" role="alert">
          <p className="text-sm text-muted">
            {t('activityDetail.participants.loadError')}
          </p>
          <button
            type="button"
            className="btn-ghost min-h-11 px-2 text-sm"
            onClick={onRetry}
          >
            {t('activityDetail.participants.retry')}
          </button>
        </div>
      ) : null}

      {status === 'ready' && participants.length === 0 ? (
        <p className="mt-3 flex items-center gap-2 text-sm text-muted">
          <UsersThree
            size={18}
            weight={kuniriIconWeight}
            aria-hidden="true"
          />
          {t('activityDetail.participants.empty')}
        </p>
      ) : null}

      {status === 'ready' && participants.length > 0 ? (
        <ul className="mt-2 divide-y divide-line">
          {participants.map((participant) => {
            const isCreator = participant.id === activity.creatorId
            const attendance = participant.attendanceStatus ?? null
            const attendanceLabel =
              attendance === 'attended'
                ? t('activityDetail.participants.attendance.attended')
                : attendance === 'absent'
                  ? t('activityDetail.participants.attendance.absent')
                  : t('activityDetail.participants.attendance.pending')
            const showAttendance = canManageAttendance && !participant.mine
            const meta = [
              participant.city,
              participant.rating != null
                ? t('activityDetail.rating', {
                    rating: formatNumber(participant.rating, locale, {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    }),
                    count: participant.ratingCount,
                  })
                : t('activityDetail.noRating'),
              showAttendance ? attendanceLabel : null,
            ]
              .filter(Boolean)
              .join(' · ')
            const canFeedback =
              feedbackAvailable(activity) &&
              !participant.mine &&
              !participant.feedbackGiven

            return (
              <li key={participant.id}>
                <div className="flex items-center gap-3 py-3">
                  <UserAvatar
                    name={formatPerson(participant)}

                    src={participant.avatarUrl}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Link
                        to={`/users/${encodeURIComponent(participant.id)}`}
                        className="text-[15px] font-semibold break-words text-deep hover:text-teal"
                      >
                        {formatPerson(participant)}
                      </Link>
                      {isCreator ? (
                        <span className="rounded-pill bg-mint px-2 py-0.5 text-[11px] font-bold text-teal">
                          {t('activityDetail.participants.creatorBadge')}
                        </span>
                      ) : null}
                      {participant.mine ? (
                        <span className="rounded-pill bg-paper px-2 py-0.5 text-[11px] font-bold text-muted ring-1 ring-line">
                          {t('activityDetail.participants.you')}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-0.5 text-xs leading-relaxed break-words text-muted">
                      {meta}
                    </p>
                  </div>

                  {!participant.mine || canManageAttendance ? (
                    <details className="relative shrink-0">
                      <summary
                        className="grid size-9 cursor-pointer list-none place-items-center rounded-full text-muted hover:bg-paper"
                        aria-label={t('activityDetail.participants.actionsFor', {
                          name: participant.publicName,
                        })}
                      >
                        <DotsThreeVertical
                          size={22}
                          weight="bold"
                          aria-hidden="true"
                        />
                      </summary>
                      <div className="absolute right-0 z-20 mt-1 w-52 rounded-md border border-line bg-white p-1 shadow-float">
                        {canManageAttendance && !participant.mine ? (
                          <>
                            <button
                              type="button"
                              className="btn-ghost min-h-10 w-full justify-start px-2 text-left text-sm"
                              disabled={attendanceBusyId === participant.id}
                              onClick={() =>
                                void setAttendance(participant, 'attended')
                              }
                            >
                              {t('activityDetail.participants.attendanceConfirm')}
                            </button>
                            <button
                              type="button"
                              className="btn-ghost min-h-10 w-full justify-start px-2 text-left text-sm"
                              disabled={attendanceBusyId === participant.id}
                              onClick={() =>
                                void setAttendance(participant, 'absent')
                              }
                            >
                              {t('activityDetail.participants.attendanceAbsent')}
                            </button>
                          </>
                        ) : null}
                        {canFeedback ? (
                          <button
                            type="button"
                            className="btn-ghost min-h-10 w-full justify-start px-2 text-left text-sm"
                            onClick={() => openAction(participant, 'feedback')}
                          >
                            {t('activityDetail.feedback.open')}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="btn-ghost min-h-10 w-full justify-start px-2 text-left text-sm"
                          onClick={() => openAction(participant, 'report')}
                        >
                          {t('activityDetail.actions.report')}
                        </button>
                        <button
                          type="button"
                          className="btn-ghost min-h-10 w-full justify-start px-2 text-left text-sm text-danger hover:bg-[var(--danger-soft)]"
                          onClick={() => openAction(participant, 'block')}
                        >
                          {t('activityDetail.actions.block', {
                            name: participant.publicName,
                          })}
                        </button>
                      </div>
                    </details>
                  ) : null}
                </div>
              </li>
            )
          })}
        </ul>
      ) : null}

      <ConfirmDialog
        open={target?.kind === 'report'}
        titleKey="activityDetail.actions.reportTitle"
        bodyKey="activityDetail.feedback.reportFor"
        bodyValues={{ name: target?.participant.publicName ?? '' }}
        confirmKey={
          busy
            ? 'activityDetail.actions.reportSubmitting'
            : 'activityDetail.actions.reportSubmit'
        }
        cancelKey="activityDetail.actions.reportCancel"
        busy={busy}
        onConfirm={() => {
          if (target) void submitReport(target.participant)
        }}
        onCancel={closeAction}
      >
        <div className="space-y-3">
          <div>
            <label htmlFor={reasonId} className="field-label text-sm">
              {t('activityDetail.actions.reportReason')}
            </label>
            <select
              id={reasonId}
              className="field-input"
              value={reason}
              aria-invalid={reasonError || undefined}
              onChange={(event) => {
                setReason(event.target.value as ReportReason | '')
                setReasonError(false)
              }}
            >
              <option value="">
                {t('activityDetail.actions.reportReasonPlaceholder')}
              </option>
              {REPORT_REASONS.map((value) => (
                <option key={value} value={value}>
                  {t(`activityDetail.actions.reasons.${value}`)}
                </option>
              ))}
            </select>
            {reasonError ? (
              <p role="alert" className="mt-1 text-xs font-semibold text-danger">
                {t('activityDetail.actions.reportReasonRequired')}
              </p>
            ) : null}
          </div>
          <div>
            <label htmlFor={descriptionId} className="field-label text-sm">
              {t('activityDetail.actions.reportDescription')}
            </label>
            <textarea
              id={descriptionId}
              className="field-input min-h-24"
              maxLength={2000}
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
          {actionError ? (
            <p role="alert" className="text-sm font-semibold text-danger">
              {t(actionError)}
            </p>
          ) : null}
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={target?.kind === 'block'}
        titleKey="activityDetail.actions.blockTitle"
        titleValues={{ name: target?.participant.publicName ?? '' }}
        bodyKey="activityDetail.actions.blockBody"
        confirmKey="activityDetail.actions.blockConfirm"
        cancelKey="activityDetail.actions.blockCancel"
        tone="danger"
        busy={busy}
        onConfirm={() => {
          if (target) void submitBlock(target.participant)
        }}
        onCancel={closeAction}
      >
        {actionError ? (
          <p role="alert" className="text-sm font-semibold text-danger">
            {t(actionError)}
          </p>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={target?.kind === 'feedback'}
        titleKey="activityDetail.feedback.prompt"
        titleValues={{ name: target?.participant.publicName ?? '' }}
        bodyKey="activityDetail.feedback.body"
        confirmKey={
          busy
            ? 'activityDetail.feedback.submitting'
            : 'activityDetail.feedback.submit'
        }
        cancelKey="activityDetail.feedback.cancel"
        busy={busy}
        onConfirm={() => {
          if (target) void submitFeedback(target.participant)
        }}
        onCancel={closeAction}
      >
        <div className="space-y-3">
          <div>
            <label htmlFor={punctualityId} className="field-label text-sm">
              {t('activityDetail.feedback.punctuality')}
            </label>
            <select
              id={punctualityId}
              className="field-input"
              value={scores.punctualityScore}
              onChange={(event) =>
                setScores((current) => ({
                  ...current,
                  punctualityScore: Number(event.target.value),
                }))
              }
            >
              {SCORES.map((score) => (
                <option key={score} value={score}>
                  {t('activityDetail.feedback.score', { score })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor={reliabilityId} className="field-label text-sm">
              {t('activityDetail.feedback.reliability')}
            </label>
            <select
              id={reliabilityId}
              className="field-input"
              value={scores.reliabilityScore}
              onChange={(event) =>
                setScores((current) => ({
                  ...current,
                  reliabilityScore: Number(event.target.value),
                }))
              }
            >
              {SCORES.map((score) => (
                <option key={score} value={score}>
                  {t('activityDetail.feedback.score', { score })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor={respectId} className="field-label text-sm">
              {t('activityDetail.feedback.respect')}
            </label>
            <select
              id={respectId}
              className="field-input"
              value={scores.respectScore}
              onChange={(event) =>
                setScores((current) => ({
                  ...current,
                  respectScore: Number(event.target.value),
                }))
              }
            >
              {SCORES.map((score) => (
                <option key={score} value={score}>
                  {t('activityDetail.feedback.score', { score })}
                </option>
              ))}
            </select>
          </div>
          {activity.viewerStatus === 'creator' ? (
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-0.5 size-5 shrink-0 accent-teal"
                checked={markAbsent}
                onChange={(event) => setMarkAbsent(event.target.checked)}
              />
              <span className="text-sm font-semibold text-deep">
                {t('activityDetail.feedback.absentCheckbox')}
              </span>
            </label>
          ) : null}
          {actionError ? (
            <p role="alert" className="text-sm font-semibold text-danger">
              {t(actionError)}
            </p>
          ) : null}
        </div>
      </ConfirmDialog>
    </section>
  )
}
